// LicensePanel — displays current license status and allows admins to
// activate / remove a license key. Shown at the top of the Settings page.
//
// Note: the "unlicensed" mode (trial expired, no key) triggers the full-page
// LicenseWall in App.tsx before this panel is ever shown. So here we handle
// "trial", "licensed", "grace", and "expired" states.
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Popconfirm,
  Progress,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { api, type LicenseMode, type LicenseStatus } from "../api";
import { useRole } from "../contexts/AuthContext";

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function modeTag(mode: LicenseMode, readOnly: boolean) {
  if (readOnly && mode !== "licensed" && mode !== "grace") {
    return <Tag color="red" icon={<StopOutlined />}>Read-Only</Tag>;
  }
  switch (mode) {
    case "licensed":    return <Tag color="green"  icon={<CheckCircleOutlined />}>Licensed</Tag>;
    case "trial":       return <Tag color="blue"   icon={<ExclamationCircleOutlined />}>Trial</Tag>;
    case "grace":       return <Tag color="orange" icon={<ExclamationCircleOutlined />}>Grace Period</Tag>;
    case "expired":     return <Tag color="red"    icon={<StopOutlined />}>Expired</Tag>;
    case "unlicensed":  return <Tag color="red"    icon={<StopOutlined />}>Unlicensed</Tag>;
    default:            return <Tag>{mode}</Tag>;
  }
}

function alertType(mode: LicenseMode, readOnly: boolean): "success" | "info" | "warning" | "error" {
  if (readOnly || mode === "expired" || mode === "unlicensed") return "error";
  if (mode === "grace") return "warning";
  if (mode === "trial") return "info";
  return "success";
}

function usagePct(used: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
}

function usageLabel(used: number, max: number): string {
  if (max <= 0) return `${used} (unlimited)`;
  return `${used} / ${max}`;
}

function usageStatus(used: number, max: number): "normal" | "active" | "exception" {
  if (max <= 0) return "normal";
  const pct = (used / max) * 100;
  if (pct >= 100) return "exception";
  if (pct >= 80)  return "active";
  return "normal";
}

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleDateString("en-CA"); // YYYY-MM-DD
}

// ---------------------------------------------------------------------------
// Installation ID box — always visible
// ---------------------------------------------------------------------------

function InstallationIDBox({ id }: { id: string }) {
  const copyID = () => {
    navigator.clipboard.writeText(id).then(
      () => message.success("Installation ID copied!"),
      () => message.error("Copy failed — please copy manually"),
    );
  };

  return (
    <div style={{
      background: "#f6ffed",
      border: "1px solid #b7eb8f",
      borderRadius: 6,
      padding: "12px 16px",
      marginBottom: 16,
    }}>
      <Text strong style={{ display: "block", marginBottom: 6, fontSize: 13 }}>
        🔑 Installation ID
      </Text>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Text
          code
          style={{ fontSize: 15, letterSpacing: 2, color: "#1a1a1a", userSelect: "all" }}
        >
          {id}
        </Text>
        <Button size="small" icon={<CopyOutlined />} onClick={copyID}>
          Copy
        </Button>
      </div>
      <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
        Gửi ID này cho vendor để nhận license key tương ứng với installation này.
      </Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activate / remove form (admin only)
// ---------------------------------------------------------------------------

interface ActivateFormProps {
  status: LicenseStatus;
  onUpdate: (s: LicenseStatus) => void;
}

function ActivateForm({ status, onUpdate }: ActivateFormProps) {
  const [key, setKey]           = useState("");
  const [saving, setSaving]     = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleActivate = async () => {
    if (!key.trim()) {
      message.error("Please paste a license key");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.activateLicense(key.trim());
      onUpdate(updated);
      setKey("");
      message.success("License activated successfully!");
    } catch (e) {
      if (e instanceof Error) message.error("Activation failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const updated = await api.removeLicense();
      onUpdate(updated);
      message.warning("License removed — system is now unlicensed and blocked.");
    } catch (e) {
      if (e instanceof Error) message.error("Remove failed: " + e.message);
    } finally {
      setRemoving(false);
    }
  };

  const hasLicense = status.mode === "licensed" || status.mode === "grace";

  return (
    <div style={{ marginTop: 16 }}>
      <Text strong style={{ display: "block", marginBottom: 8 }}>
        {hasLicense ? "Replace license key:" : "Activate license key:"}
      </Text>
      <Input.TextArea
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Paste SFLOW-… license key here"
        rows={3}
        style={{ fontFamily: "monospace", fontSize: 12, maxWidth: 640 }}
      />
      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button
          type="primary"
          icon={<KeyOutlined />}
          onClick={handleActivate}
          loading={saving}
          disabled={!key.trim()}
        >
          Activate
        </Button>
        {hasLicense && (
          <Popconfirm
            title="Remove license?"
            description="Hệ thống sẽ bị khóa hoàn toàn cho đến khi kích hoạt lại license mới. Tiếp tục?"
            onConfirm={handleRemove}
            okText="Remove"
            okButtonProps={{ danger: true }}
          >
            <Button danger loading={removing}>
              Remove License
            </Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function LicensePanel() {
  const { isAdmin } = useRole();
  const [status, setStatus]   = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setStatus(await api.licenseStatus());
    } catch (e) {
      if (e instanceof Error) message.error("Failed to load license status: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !status) {
    return (
      <Card
        size="small"
        loading
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span style={{ fontWeight: 700 }}>License</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
      />
    );
  }

  const type = alertType(status.mode, status.read_only);

  return (
    <Card
      size="small"
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span style={{ fontWeight: 700 }}>License</span>
          {modeTag(status.mode, status.read_only)}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {/* Installation ID — always shown */}
      {status.installation_id && (
        <InstallationIDBox id={status.installation_id} />
      )}

      {/* Status message banner */}
      {status.message && (
        <Alert
          type={type}
          showIcon
          message={status.message}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Key details */}
      <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
        {status.customer && (
          <Descriptions.Item label="Customer" span={2}>
            <Text strong>{status.customer}</Text>
          </Descriptions.Item>
        )}
        {status.expires_at && (
          <Descriptions.Item label="Expires">
            <Text>{fmtDate(status.expires_at)}</Text>
            {" "}
            {status.days_remaining >= 0
              ? <Tag color="blue">{status.days_remaining} day(s) left</Tag>
              : <Tag color="red">{-status.days_remaining} day(s) ago</Tag>
            }
          </Descriptions.Item>
        )}
        {status.mode === "trial" && (
          <Descriptions.Item label="Trial ends in">
            <Tag color={status.days_remaining <= 3 ? "red" : "blue"}>
              {status.days_remaining} day(s)
            </Tag>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Mode">
          {status.mode === "licensed"   && <Text type="success">Full access</Text>}
          {status.mode === "trial"      && <Text>Trial — full access for {status.days_remaining} more day(s)</Text>}
          {status.mode === "grace"      && <Text type="warning">Grace period (read-write)</Text>}
          {status.mode === "expired"    && <Text type="danger">Expired — read-only</Text>}
          {status.mode === "unlicensed" && <Text type="danger">No license — system blocked</Text>}
        </Descriptions.Item>
      </Descriptions>

      {/* Usage bars — only meaningful when licensed */}
      {(status.mode === "licensed" || status.mode === "grace") && (
        <div style={{ maxWidth: 480, marginBottom: 8 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <Text style={{ fontSize: 13 }}>Sensors</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {usageLabel(status.used_sensors, status.max_sensors)}
              </Text>
            </div>
            {status.max_sensors > 0 ? (
              <Progress
                percent={usagePct(status.used_sensors, status.max_sensors)}
                status={usageStatus(status.used_sensors, status.max_sensors)}
                size="small"
                strokeColor={
                  usagePct(status.used_sensors, status.max_sensors) >= 100
                    ? "#ff4d4f"
                    : usagePct(status.used_sensors, status.max_sensors) >= 80
                    ? "#fa8c16"
                    : "#52c41a"
                }
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>Unlimited</Text>
            )}
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <Text style={{ fontSize: 13 }}>IP Zone Entries</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {usageLabel(status.used_ips, status.max_ips)}
              </Text>
            </div>
            {status.max_ips > 0 ? (
              <Progress
                percent={usagePct(status.used_ips, status.max_ips)}
                status={usageStatus(status.used_ips, status.max_ips)}
                size="small"
                strokeColor={
                  usagePct(status.used_ips, status.max_ips) >= 100
                    ? "#ff4d4f"
                    : usagePct(status.used_ips, status.max_ips) >= 80
                    ? "#fa8c16"
                    : "#52c41a"
                }
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>Unlimited</Text>
            )}
          </div>
        </div>
      )}

      {/* Activate form — admin only */}
      {isAdmin && <ActivateForm status={status} onUpdate={setStatus} />}

      {!isAdmin && status.read_only && (
        <Alert
          type="warning"
          showIcon
          message="Contact your administrator to activate a license."
          style={{ marginTop: 12 }}
        />
      )}
    </Card>
  );
}
